import React from "react";
import { Box, IconButton } from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import { MapLocation } from "../../models/models";
import MapRequestCard from "./MapRequestCard";
import useTranslation from "../../../../shared/hooks/useTranslation";
import { dt } from "../../../../shared/themes/redesignTokens";

// ═══════════════════════════════════════════════════════════════
//  DESIGN TOKENS
// ═══════════════════════════════════════════════════════════════


// ═══════════════════════════════════════════════════════════════
//  PRIORITY CHIP CONFIG
// ═══════════════════════════════════════════════════════════════

interface PriorityChipConfig {
  labelKey: string;
  bg: string;
  text: string;
  border: string;
}

const priorityChips: { key: keyof Pick<MapLocation, "urgentCount" | "highCount" | "lowCount">; config: PriorityChipConfig }[] = [
  {
    key: "urgentCount",
    config: { labelKey: "urgent", bg: "#F9EBEA", text: "#C0392B", border: "#F5B7B1" },
  },
  {
    key: "highCount",
    config: { labelKey: "priorityHigh", bg: "#FFF0EB", text: "#E67E22", border: "#F5CBA7" },
  },
  {
    key: "lowCount",
    config: { labelKey: "priorityLow", bg: "#EAFAF1", text: "#27AE60", border: "#ABEBC6" },
  },
];

// ═══════════════════════════════════════════════════════════════
//  COMPONENT
// ═══════════════════════════════════════════════════════════════

type SidebarMode = "side" | "bottomSheet" | "fullscreen";

interface CitySidebarProps {
  location: MapLocation | null;
  onClose: () => void;
  mode?: SidebarMode;
  currentRequestId?: string;
  onCurrentRequestClick?: () => void;
}

const CitySidebar: React.FC<CitySidebarProps> = ({ location, onClose, mode = "side", currentRequestId, onCurrentRequestClick }) => {
  const { translate } = useTranslation(["serviceBoard"]);
  const isOpen = location !== null;

  // Desktop: right slide-in panel
  const sideSx = {
    position: "absolute" as const,
    top: 0,
    right: 0,
    bottom: 0,
    width: "380px",
    maxWidth: "100%",
    backgroundColor: dt.cardBg,
    borderLeft: `1px solid ${dt.cardBorder}`,
    boxShadow: isOpen ? "0 -4px 20px rgba(0,0,0,0.06)" : "none",
    transform: isOpen ? "translateX(0)" : "translateX(100%)",
    transition: "transform 300ms cubic-bezier(0.4, 0, 0.2, 1)",
    zIndex: 20,
    display: "flex",
    flexDirection: "column" as const,
    overflow: "hidden" as const,
  };

  // Tablet: bottom sheet that slides up
  const bottomSheetSx = {
    position: "absolute" as const,
    left: 0,
    right: 0,
    bottom: 0,
    width: "100%",
    maxHeight: "60vh",
    backgroundColor: dt.cardBg,
    borderTop: `1px solid ${dt.cardBorder}`,
    borderRadius: "16px 16px 0 0",
    boxShadow: isOpen ? "0 -8px 30px rgba(0,0,0,0.12)" : "none",
    transform: isOpen ? "translateY(0)" : "translateY(100%)",
    transition: "transform 300ms cubic-bezier(0.4, 0, 0.2, 1)",
    zIndex: 20,
    display: "flex",
    flexDirection: "column" as const,
    overflow: "hidden" as const,
  };

  // Phone: full-screen overlay that slides up
  const fullscreenSx = {
    position: "fixed" as const,
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    width: "100%",
    height: "100%",
    backgroundColor: dt.cardBg,
    boxShadow: isOpen ? "0 0 40px rgba(0,0,0,0.2)" : "none",
    transform: isOpen ? "translateY(0)" : "translateY(100%)",
    transition: "transform 300ms cubic-bezier(0.4, 0, 0.2, 1)",
    zIndex: 1300,
    display: "flex",
    flexDirection: "column" as const,
    overflow: "hidden" as const,
  };

  const containerSx =
    mode === "fullscreen" ? fullscreenSx : mode === "bottomSheet" ? bottomSheetSx : sideSx;

  return (
    <Box sx={containerSx}>
      {/* Drag handle for bottom sheet mode */}
      {mode === "bottomSheet" && isOpen && (
        <Box
          sx={{
            display: "flex",
            justifyContent: "center",
            pt: "10px",
            pb: "4px",
            flexShrink: 0,
            cursor: "grab",
          }}
        >
          <Box
            sx={{
              width: "36px",
              height: "4px",
              borderRadius: "2px",
              backgroundColor: "#D1D5DB",
            }}
          />
        </Box>
      )}

      {/* Header */}
      {location && (
        <>
          <Box
            sx={{
              flexShrink: 0,
              backgroundColor: dt.headerBg,
              borderBottom: `1px solid ${dt.cardBorder}`,
              padding: "16px",
              ...(mode === "fullscreen" && {
                position: "sticky" as const,
                top: 0,
                zIndex: 1,
              }),
            }}
          >
            {/* Title row */}
            <Box
              sx={{
                display: "flex",
                alignItems: "flex-start",
                justifyContent: "space-between",
                mb: "8px",
              }}
            >
              <Box>
                <Box
                  sx={{
                    fontFamily: dt.font,
                    fontSize: "16px",
                    fontWeight: 700,
                    color: dt.textPrimary,
                    lineHeight: 1.3,
                  }}
                >
                  {location.city}
                </Box>
                <Box
                  sx={{
                    fontFamily: dt.font,
                    fontSize: "12px",
                    color: dt.textSecondary,
                    mt: "2px",
                  }}
                >
                  {location.countryName}
                </Box>
              </Box>
              <IconButton
                onClick={onClose}
                size="small"
                aria-label={translate("sidebarClose")}
                sx={{
                  color: dt.textMuted,
                  mt: "-4px",
                  mr: "-4px",
                  "&:hover": {
                    color: dt.textPrimary,
                    backgroundColor: "rgba(0,0,0,0.04)",
                  },
                }}
              >
                <CloseIcon fontSize="small" />
              </IconButton>
            </Box>

            {/* Request count */}
            <Box
              sx={{
                fontFamily: dt.fontMono,
                fontSize: "13px",
                fontWeight: 600,
                color: dt.textPrimary,
                mb: "10px",
              }}
            >
              {translate("sidebarRequests", { count: location.totalCount })}
            </Box>

            {/* Priority breakdown chips */}
            <Box
              sx={{
                display: "flex",
                flexWrap: "wrap",
                gap: "6px",
              }}
            >
              {priorityChips.map(({ key, config }) => {
                const count = location[key];
                if (count === 0) return null;
                return (
                  <Box
                    key={key}
                    sx={{
                      display: "inline-flex",
                      alignItems: "center",
                      px: "8px",
                      py: "3px",
                      borderRadius: "4px",
                      backgroundColor: config.bg,
                      border: `1px solid ${config.border}`,
                      fontFamily: dt.font,
                      fontSize: "11px",
                      fontWeight: 600,
                      color: config.text,
                      lineHeight: 1.3,
                    }}
                  >
                    {count} {translate(config.labelKey)}
                  </Box>
                );
              })}
            </Box>
          </Box>

          {/* Scrollable request list */}
          <Box
            sx={{
              flex: 1,
              overflowY: "auto",
              overflowX: "hidden",
              padding: "12px",
              display: "flex",
              flexDirection: "column",
              gap: "12px",
            }}
          >
            {location.requests.length === 0 ? (
              <Box
                sx={{
                  fontFamily: dt.font,
                  fontSize: "13px",
                  color: dt.textMuted,
                  textAlign: "center",
                  py: "24px",
                }}
              >
                {translate("sidebarNoRequests")}
              </Box>
            ) : (
              location.requests.map((request) => (
                <MapRequestCard
                  key={request.id}
                  request={request}
                  currentRequestId={currentRequestId}
                  onCurrentRequestClick={onCurrentRequestClick}
                />
              ))
            )}
          </Box>
        </>
      )}
    </Box>
  );
};

export default React.memo(CitySidebar);
